import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';
import {
  Loader2, CheckCircle2, ChevronLeft, ChevronRight, ThumbsUp, ThumbsDown,
  Eraser, Camera, Image as ImageIcon, AlertCircle,
} from 'lucide-react';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Textarea } from '../../ui/textarea';
import { toast } from '../../../hooks/use-toast';
import AudioInstruction from '../AudioInstruction';

const PARTY_LABELS = {
  shipper: 'remettre la marchandise au camion',
  consignee: 'recevoir la marchandise du camion',
};

/**
 * PM Spec V3 13/05/2026 — SiteRepFlow
 *
 * Parcours ULTRA simplifié pour SHIPPER ou CONSIGNEE (illettrés).
 * 3 écrans :
 *   1. Question oui/non + audio + (si non, capture problème)
 *   2. Photo simple (caméra ou galerie)
 *   3. Signature plein écran
 *
 * Props :
 *   type: 'loading' | 'unloading'
 *   party: 'shipper' | 'consignee'
 *   operation, operatorName
 *   endpointPrefix, authHeaders
 *   linkId
 *   onCompleted()
 */
const SiteRepFlow = ({
  type, party, operation, operatorName,
  endpointPrefix, authHeaders, linkId, noteId,
  onCompleted,
}) => {
  const isLoading = type === 'loading';
  // Bug fix 16/02/2026 — Stabilise axios.create via useState lazy init
  const [ax] = useState(() => axios.create({ headers: authHeaders || {} }));
  const [step, setStep] = useState(1); // 1 question, 2 photo, 3 signature, 4 done
  const [allOk, setAllOk] = useState(null); // true/false/null
  const [problemNote, setProblemNote] = useState('');
  const [photo, setPhoto] = useState(null); // {dataUrl, fileSize}
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const sigRef = useRef(null);
  const noteRef = useRef(null); // Loading note record (created on demand)

  // Charge ou crée le bon
  // PM Bug fix 17/02/2026 — Priorité au noteId scopé (JWT) : `signatureLinks` est
  // strippé du list public donc le filtre par linkId ne fonctionne pas en multi-rotation.
  // Le noteId est désormais fourni directement par exchange-link-token.
  const ensureNote = useCallback(async () => {
    if (noteRef.current) return noteRef.current;
    const list = await ax.get(endpointPrefix);
    const allNotes = list.data || [];
    let existing;
    // Priorité 1 : noteId fourni par le scope JWT (multi-rotation safe)
    if (noteId) {
      existing = allNotes.find(n => n.id === noteId);
    }
    // Priorité 2 (fallback legacy): match par linkId si signatureLinks exposé
    if (!existing && linkId) {
      existing = allNotes.find(n =>
        (n.signatureLinks || []).some(l => l.id === linkId)
      );
    }
    // Priorité 3 : 1er bon du bon type
    if (!existing) {
      existing = allNotes.find(n => n.type === type);
    }
    if (existing) {
      noteRef.current = existing;
      return existing;
    }
    const created = await ax.post(endpointPrefix, { type });
    noteRef.current = created.data;
    return created.data;
  }, [endpointPrefix, type, ax, linkId, noteId]);

  useEffect(() => { ensureNote().catch(() => {}); }, [ensureNote]);

  const compressFile = async (file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        const ratio = Math.min(1, 1280 / img.width);
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * ratio);
        canvas.height = Math.round(img.height * ratio);
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        URL.revokeObjectURL(url);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
        resolve({ dataUrl, fileSize: Math.round(dataUrl.length * 0.75) });
      };
      img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('IMG_ERROR')); };
      img.src = url;
    });
  };

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const compressed = await compressFile(f);
      setPhoto({ ...compressed, source: 'camera_live' });
    } catch {
      toast({ title: 'Photo non chargée', description: 'Réessayez', variant: 'destructive' });
    }
  };

  const submit = async () => {
    if (!sigRef.current || sigRef.current.isEmpty()) {
      toast({ title: 'Signature requise', description: 'Signez avant de valider', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      const note = await ensureNote();
      const noteId = note.id;
      // 1) Add photo if present
      if (photo) {
        await ax.post(`${endpointPrefix}/${noteId}/photos`, {
          label: isLoading ? 'marchandise' : 'marchandise_recue',
          base64: photo.dataUrl,
          fileSize: photo.fileSize,
          source: photo.source || 'camera_live',
          replaceByLabel: true,   // Bug fix 16/02 — idempotence re-tries
        });
      }
      // 2) Update reserveDescription if not OK
      if (allOk === false) {
        const stateText = `Réserves émises${problemNote ? ': ' + problemNote : ''}`;
        const patch = isLoading
          ? { actualState: stateText, observations: problemNote || stateText }
          : { reserveDescription: problemNote || stateText, actualState: stateText };
        await ax.patch(`${endpointPrefix}/${noteId}`, patch);
      } else {
        const patch = isLoading
          ? { actualState: 'Bon état', observations: 'Marchandise remise conforme' }
          : { actualState: 'Bon état', observations: 'Marchandise reçue conforme' };
        await ax.patch(`${endpointPrefix}/${noteId}`, patch);
      }
      // 3) Submit signature
      const sigData = sigRef.current.getCanvas().toDataURL('image/png');
      await ax.post(`${endpointPrefix}/${noteId}/sign`, {
        party,
        name: operatorName || 'Représentant',
        signatureBase64: sigData,
        channel: 'magic_link',
        linkId: linkId || undefined,
      });
      setSuccess(true);
      setStep(4);
      onCompleted?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Erreur inconnue';
      toast({ title: 'Échec de validation', description: detail, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Step 4 : Success ───
  if (step === 4 || success) {
    return (
      <Card className="border-emerald-200 bg-emerald-50" data-testid="siterep-success">
        <CardContent className="p-8 text-center space-y-3">
          <div className="mx-auto w-16 h-16 rounded-full bg-emerald-600 flex items-center justify-center">
            <CheckCircle2 className="w-9 h-9 text-white" />
          </div>
          <div className="text-xl font-bold text-emerald-800">Merci, c'est validé !</div>
          <div className="text-sm text-slate-600">Votre signature a bien été enregistrée.</div>
        </CardContent>
      </Card>
    );
  }

  // ─── Step 1 : Question oui/non ───
  if (step === 1) {
    const question = `Avez-vous bien ${PARTY_LABELS[party] || 'effectué l\'opération'} ?`;
    return (
      <Card data-testid="siterep-step-1">
        <CardContent className="p-5 space-y-5">
          <div className="space-y-2">
            <Badge className="bg-emerald-600 text-white border-0">Étape 1 / 3</Badge>
            <div className="text-xl font-bold text-slate-900 leading-tight">
              {question}
            </div>
            <AudioInstruction text={question} testId="siterep-step1-audio" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              data-testid="siterep-yes-btn"
              onClick={() => { setAllOk(true); setStep(2); }}
              className="rounded-2xl border-4 border-emerald-300 bg-emerald-50 hover:bg-emerald-100 p-6 flex flex-col items-center justify-center gap-2 min-h-[140px] transition-all active:scale-95"
            >
              <ThumbsUp className="w-12 h-12 text-emerald-600" />
              <span className="text-lg font-bold text-emerald-700">OUI</span>
            </button>
            <button
              type="button"
              data-testid="siterep-no-btn"
              onClick={() => { setAllOk(false); setStep(2); }}
              className="rounded-2xl border-4 border-red-300 bg-red-50 hover:bg-red-100 p-6 flex flex-col items-center justify-center gap-2 min-h-[140px] transition-all active:scale-95"
            >
              <ThumbsDown className="w-12 h-12 text-red-600" />
              <span className="text-lg font-bold text-red-700">SOUCI</span>
            </button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // ─── Step 2 : Photo ───
  if (step === 2) {
    const photoLabel = allOk === false
      ? 'Photo du problème'
      : isLoading ? 'Photo de la marchandise' : 'Photo de la marchandise reçue';
    return (
      <Card data-testid="siterep-step-2">
        <CardContent className="p-5 space-y-5">
          <div className="space-y-2">
            <Badge className="bg-emerald-600 text-white border-0">Étape 2 / 3</Badge>
            <div className="text-xl font-bold text-slate-900 leading-tight">
              {photoLabel}
            </div>
            <AudioInstruction text={`Prenez une photo. ${photoLabel}.`} testId="siterep-step2-audio" />
          </div>

          {allOk === false && (
            <div className="space-y-2">
              <Textarea
                data-testid="siterep-problem-note"
                placeholder="Que s'est-il passé ? (optionnel)"
                value={problemNote}
                onChange={(e) => setProblemNote(e.target.value)}
                className="min-h-[80px]"
              />
            </div>
          )}

          {photo ? (
            <div className="relative">
              <img src={photo.dataUrl} alt="Aperçu"
                   className="w-full h-56 object-cover rounded-lg border border-slate-200" />
              <Button
                size="sm" variant="destructive"
                data-testid="siterep-photo-remove"
                onClick={() => setPhoto(null)}
                className="absolute top-2 right-2 h-7"
              >
                Refaire
              </Button>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-2">
              <input ref={cameraRef} type="file" accept="image/*" capture="environment"
                     onChange={handleFile} className="hidden" data-testid="siterep-camera-input" />
              <input ref={galleryRef} type="file" accept="image/*"
                     onChange={handleFile} className="hidden" data-testid="siterep-gallery-input" />
              <Button size="lg"
                      data-testid="siterep-camera-btn"
                      onClick={() => cameraRef.current?.click()}
                      className="flex-1 h-16 bg-emerald-600 hover:bg-emerald-700 gap-2 text-base font-semibold">
                <Camera className="w-6 h-6" /> Prendre maintenant
              </Button>
              <Button size="lg" variant="outline"
                      data-testid="siterep-gallery-btn"
                      onClick={() => galleryRef.current?.click()}
                      className="flex-1 h-16 gap-2 text-base font-semibold">
                <ImageIcon className="w-6 h-6" /> Galerie
              </Button>
            </div>
          )}

          <div className="flex justify-between pt-2">
            <Button variant="ghost" size="sm" onClick={() => setStep(1)}
                    data-testid="siterep-step2-back">
              <ChevronLeft className="w-4 h-4 mr-1" /> Retour
            </Button>
            <Button size="lg"
                    data-testid="siterep-step2-next"
                    disabled={!photo}
                    onClick={() => setStep(3)}
                    className="bg-emerald-600 hover:bg-emerald-700">
              Continuer <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // ─── Step 3 : Signature ───
  return (
    <Card data-testid="siterep-step-3">
      <CardContent className="p-5 space-y-4">
        <div className="space-y-2">
          <Badge className="bg-emerald-600 text-white border-0">Étape 3 / 3</Badge>
          <div className="text-xl font-bold text-slate-900 leading-tight">
            Signez ici{operatorName ? `, ${operatorName}` : ''}
          </div>
          <AudioInstruction text={`Signez ici avec votre doigt`} testId="siterep-step3-audio" />
        </div>

        <div className="border-2 border-dashed border-emerald-300 rounded-lg bg-white">
          <SignatureCanvas
            ref={sigRef}
            penColor="#0f172a"
            canvasProps={{
              className: 'w-full h-56 rounded-lg',
              'data-testid': 'siterep-signature-canvas',
            }}
          />
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm"
                  data-testid="siterep-clear-sig"
                  onClick={() => sigRef.current?.clear()}
                  className="gap-1.5">
            <Eraser className="w-3.5 h-3.5" /> Effacer
          </Button>
          <span className="text-xs text-slate-500 italic">Tracez votre signature avec le doigt</span>
        </div>

        <div className="flex justify-between pt-2">
          <Button variant="ghost" size="sm" onClick={() => setStep(2)}
                  data-testid="siterep-step3-back">
            <ChevronLeft className="w-4 h-4 mr-1" /> Retour
          </Button>
          <Button size="lg"
                  data-testid="siterep-submit-btn"
                  disabled={submitting}
                  onClick={submit}
                  className="bg-emerald-600 hover:bg-emerald-700 gap-2 h-14 px-6 text-base font-bold">
            {submitting ? (<><Loader2 className="w-5 h-5 animate-spin" /> Enregistrement…</>)
                       : (<><CheckCircle2 className="w-5 h-5" /> J'ai signé</>)}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default SiteRepFlow;
